import { useEffect, useLayoutEffect, useRef, useState } from "react";

const COMPARE_ROW_FALLBACK_HEIGHT = 500;

/** 마운트 존 진입(그린다) / 유지 존 이탈(버린다) — 두 경계는 서로 다르다. */
export type CompareRowZoneEvent = "enterMountZone" | "leaveKeepZone";

export type CompareRowIntersectionHandler = (
  event: CompareRowZoneEvent,
) => void;

export type ObserveCompareRow = (
  element: HTMLDivElement,
  handler: CompareRowIntersectionHandler,
) => () => void;

/**
 * 무거운 비교 행을 viewport 근처에서만 유지한다.
 *
 * 마운트된 행의 높이는 ref에만 기록한다. 사이드바 고정처럼 본문 폭이 애니메이션으로
 * 바뀌는 동안 ResizeObserver가 연속 호출되어도 React 렌더를 일으키지 않는다.
 * 행을 실제로 unmount하는 순간의 렌더에서만 마지막 측정 높이를 placeholder에 쓴다.
 */
export const useVirtualizedCompareRow = ({
  observeRow,
  initiallyMounted = false,
  forceMounted = false,
  onNearViewport,
}: {
  observeRow: ObserveCompareRow;
  initiallyMounted?: boolean;
  forceMounted?: boolean;
  onNearViewport?: () => void;
}) => {
  const rowRef = useRef<HTMLDivElement>(null);
  const measuredHeightRef = useRef<number | null>(null);
  const [isMounted, setIsMounted] = useState(initiallyMounted || forceMounted);

  useEffect(() => {
    if (forceMounted) setIsMounted(true);
  }, [forceMounted]);

  useEffect(() => {
    const element = rowRef.current;
    if (!element) return;

    return observeRow(element, (event) => {
      if (event === "enterMountZone") {
        setIsMounted(true);
        onNearViewport?.();
        return;
      }

      // 최초 실측 전에는 추정 높이로 되돌리지 않는다. 이미 화면에 그린 행의
      // 실제 높이를 잃으면 아래 모든 목차 좌표가 움직여 스크롤이 튄다.
      if (!forceMounted && measuredHeightRef.current !== null) {
        setIsMounted(false);
      }
    });
  }, [forceMounted, observeRow, onNearViewport]);

  useLayoutEffect(() => {
    if (!isMounted) return;
    const element = rowRef.current;
    if (!element) return;

    const measure = () => {
      measuredHeightRef.current = Math.max(
        Math.ceil(element.getBoundingClientRect().height),
        1,
      );
    };

    measure();
    const observer =
      typeof ResizeObserver === "undefined"
        ? null
        : new ResizeObserver(measure);
    observer?.observe(element);
    return () => observer?.disconnect();
  }, [isMounted]);

  return {
    rowRef,
    isMounted,
    placeholderStyle: !isMounted
      ? {
          height: measuredHeightRef.current ?? COMPARE_ROW_FALLBACK_HEIGHT,
        }
      : undefined,
  };
};
